import { useState, useEffect, useRef } from 'react';

import { useRouter } from 'next/router';
import { Link as Slink } from 'react-scroll';
import { Container } from 'reactstrap';

import { freeAssessmentClicked } from '../../helpers/segment';
import useI18n from '../../hooks/use-i18n';
import { countryMap } from '../../language.config';
import { obt3EnabledMarkets } from '../../links.config';
import { useUtmParam as useUtmData } from '../../utils/utils';

import '../../styles/subbar.scss';

export default function JpHomePageSubbar() {
  const i18n = useI18n();
  const dropdown = useRef();
  const router = useRouter();

  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const query = router && router.query;

  const homePageSubbar = i18n.t('new_landing_page.navbar');

  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, toggleIsMenuOpen] = useState(false);
  const [isSticky, toggleIsSticky] = useState(false);
  const [selectedOption, setSelectedOption] = useState(homePageSubbar.navbar_options.option_one);
  const [utmParams, setUtmParams] = useState('');

  const subbarOptions = [{
    content: homePageSubbar.navbar_options.option_one,
    translationKey: 'option_one',
    urlEndpoint: '',
    sectionName: 'why-zenyum-details'
  },
  {
    content: homePageSubbar.navbar_options.option_six,
    translationKey: 'option_six',
    urlEndpoint: '',
    sectionName: 'treatment-technology'
  }, {
    content: homePageSubbar.navbar_options.option_two,
    translationKey: 'option_two',
    urlEndpoint: '',
    sectionName: 'before-and-after'
  }, {
    content: homePageSubbar.navbar_options.option_three,
    translationKey: 'option_three',
    urlEndpoint: '',
    sectionName: 'treatment-process'
  }, {
    content: homePageSubbar.navbar_options.option_four,
    translationKey: 'option_four',
    urlEndpoint: '',
    sectionName: 'pricing'
  },
  {
    content: homePageSubbar.navbar_options.option_five,
    translationKey: 'option_five',
    urlEndpoint: '',
    sectionName: 'smile-journey'
  },
  {
    content: homePageSubbar.navbar_options.option_seven,
    translationKey: 'option_seven',
    urlEndpoint: '',
    sectionName: 'clinic-selection'
  },
  {
    content: homePageSubbar.navbar_options.option_eight,
    translationKey: 'option_eight',
    urlEndpoint: '',
    sectionName: 'faq'
  }
  ];

  const checkSectionAvailable = (value) => {
    let selectedSection = '';
    subbarOptions.forEach((item) => {
      if (value === item.sectionName) {
        selectedSection = item.content;
      }
    });
    return selectedSection || selectedOption;
  };

  const scrollEventListner = () => {
    toggleIsSticky(window.pageYOffset > 70);
  };

  const handleClick = (e) => {
    if (dropdown.current && dropdown.current.contains(e.target)) {
      return;
    }
    toggleIsMenuOpen(false);
  };

  function valueChange() {
    setIsMobile(window.innerWidth < 1025.9);
  }

  useEffect(() => {
    setTimeout(() => {
      setUtmParams(useUtmData('Invisible%20Braces%20Page'));
    }, 50);
  }, [query]);

  useEffect(() => {
    valueChange();
    if (isMenuOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'visible';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    window.addEventListener('scroll', scrollEventListner);
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('innerwidth', valueChange);
    return () => {
      window.removeEventListener('scroll', scrollEventListner);
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('innerwidth', valueChange);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.id.length > 0) {
              const activeSection = checkSectionAvailable(entry.target.id);
              setSelectedOption(activeSection);
            }
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      });
      const sections = document.querySelectorAll('section');
      sections.forEach((section) => {
        sectionObserver.observe(section);
      });
      return () => {
        sections.forEach((section) => {
          sectionObserver.unobserve(section);
        });
      };
    }, 30);
  }, []);

  return (
    <>
      <section className={`product-secondary-navbar product-subbar-sm ${isMenuOpen
        ? 'active menu-active' : ''} ${isSticky ? 'sticky' : ''} jp-navbar`}
      >
        <Container className="jp-subbar">
          <div className="d-flex align-items-center justify-content-between">
            <div className="secondary-right">
              <ul className="sec-nav-menu-new active desktop-list">
                {subbarOptions.map((item) => (
                  <li className="desktop-options" key={item.content}>
                    <Slink
                      to={item.sectionName}
                      spy={true}
                      smooth={true}
                      offset={isMobile ? -95 : -110}
                      duration={500}
                      key={item.sectionName}
                    >
                      <span
                        role="presentation"
                        data-lokalise=""
                        data-key={`new_landing_page::navbar::navbar_options::${item.translationKey}`}
                      >
                        {item.content}
                      </span>
                    </Slink>
                  </li>
                ))}
              </ul>
              {isMobile && <div className={`mobile-list-modal-wrapper ${isMenuOpen ? 'active' : ''}`} />}
              <ul className="sec-nav-menu-new active mobile-list" ref={dropdown}>
                <li className="subbar-mobile-menu">
                  {isMenuOpen && (
                    <img
                      src="/img/jp-home-page/icons/close-button.svg"
                      alt="close-button"
                      role="presentation"
                      data-gumlet="false"
                      onClick={() => { toggleIsMenuOpen(false); }}
                    />
                  )}
                  {
                    !isMenuOpen && (
                      <span
                        role="presentation"
                        onClick={() => { toggleIsMenuOpen(true); }}
                      >
                        {selectedOption}
                      </span>
                    )
                  }
                  {isMenuOpen && (
                    <div className="subbar-dropdown-menu">
                      <ul className="menu-list">
                        {subbarOptions.map((item) => (
                          <li
                            className={`menu-options ${selectedOption === item.content ? 'selected-option' : ''}`}
                            role="presentation"
                            onClick={() => { setSelectedOption(item.content); toggleIsMenuOpen(false); }}
                            key={item.content}
                            data-key={`new_landing_page::navbar::navbar_options::${item.translationKey}`}
                            data-lokalise=""
                          >
                            <Slink
                              to={item.sectionName}
                              spy={true}
                              smooth={true}
                              offset={isMobile ? -95 : -110}
                              duration={500}
                              key={item.sectionName}
                            >
                              {item.content}
                            </Slink>

                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              </ul>
            </div>
            {!isMenuOpen && (
            <a
              className="link-button"
              onClick={() => { freeAssessmentClicked(countryMap[country], 'Aligner Sub-Header'); }}
              href={`${obt3EnabledMarkets.includes(country) ? process.env.NEXT_PUBLIC_OBT3_LINK
                : process.env.NEXT_PUBLIC_OBT_LINK}${country}-${lang}/hello-screen/${utmParams}`}
              data-key="new_landing_page::navbar::free_assessment"
              data-lokalise=""
            >
              {homePageSubbar.free_assessment}
            </a>
            )}
          </div>
        </Container>
      </section>
    </>
  );
}
